import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {Observable} from 'rxjs';
import {CountryChartData} from '../models/country-chart-data.interface';
import {ChartsSearchInterface} from '../models/charts-search.interface';
import {Master} from '../../admin/models/master.interface';
import {RecycleChartData} from '../models/recycle-chart-data.interface';
import {CostChartInfo} from '../models/cost-chart-info.interface';
import {ReturnRateWeeeSystemInfo} from '../models/return-rate-weee-system-info.interface';
import {CostBenchmarkData} from '../models/cost-benchmark-data.interface';
import {VctCollectedChartData} from '../models/vct-collected-chart-data.interface';
import {CountryChartInfo} from '../models/country-chart-info.interface';

const CHARTS_API = environment.apiUrl + 'charts';
const CATEGORIES_API_KFC = environment.apiUrl + 'kf-categories';
const CATEGORIES_API_CS = environment.apiUrl + 'collection-streams';

@Injectable({
  providedIn: 'root'
})
export class KfChartsService {

  constructor(private http: HttpClient) {
  }

  static buildSearchParams(search: ChartsSearchInterface): string {
    let searchParams = '';
    if (search.categoryType && search.categoryType !== '') {
      searchParams += `?categoryType=${search.categoryType}`;
    } else {
      searchParams += `?categoryType=NEW`;
    }
    if (search.startYear) {
      searchParams += `&startYear=${search.startYear}`;
    }
    if (search.years && search.years.length > 0) {
      search.years.forEach(year => searchParams += `&years=${year}`);
    }
    if (search.categories && search.categories.length > 0) {
      if (Array.isArray(search.categories)) {
        search.categories.forEach(category => searchParams += `&categories=${category}`);
      } else {
        searchParams += `&catgories=${search.categories}`;
      }
    }
    if (search.types && search.types.length > 0) {
      if (Array.isArray(search.categories)) {
        search.types.forEach(t => searchParams += `&types=${t}`);
      } else {
        searchParams += `&types=${search.types}`;
      }
    }
    if (search.detailLevel && search.detailLevel !== '') {
      searchParams += `&detailLevel=${search.detailLevel}`;
    }
    if (search.b2bB2c && search.b2bB2c !== '') {
      searchParams += `&kind=${search.b2bB2c}`;
    }
    if (search.member && search.member !== '') {
      searchParams += `&member=${search.member}`;
    }
    if (search.region && search.region !== '') {
      searchParams += `&region=${search.region}`;
    }
    if (search.order && search.order !== '') {
      searchParams += `&order=${search.order}`;
    }
    if (search.type && search.type !== '') {
      searchParams += `&type=${search.type}`;
    }
    if (search.costCategories && search.costCategories.length > 0) {
      search.costCategories.forEach(category => searchParams += `&costCategories=${category}`);
    }
    if (search.countries && search.countries.length > 0) {
      search.countries.forEach(country => searchParams += `&countries=${country}`);
    }
    if (search.displayUnits && search.displayUnits !== '') {
      searchParams += `&displayUnits=${search.displayUnits}`;
    }
    return searchParams;
  }


  getYears(keyFigures: string, schemaKind: string, categoryType = 'NEW'): Observable<number[]> {
    return this.http.get<number[]>(`${CHARTS_API}/years?keyFigures=${keyFigures}&schemaKind=${schemaKind}&categoryType=${categoryType}`);
  }

  getPomCategories(): Observable<Master[]> {
    return this.http.get<Master[]>(`${CATEGORIES_API_KFC}/list`);
  }

  getOldPomCategories(): Observable<Master[]> {
    return this.http.get<Master[]>(`${CATEGORIES_API_KFC}/old`);
  }

  getCollectionStreams(): Observable<Master[]> {
    return this.http.get<Master[]>(`${CATEGORIES_API_CS}/list`);
  }

  getEuropeanTrend(search: ChartsSearchInterface, keyFigures: string): Observable<CountryChartData[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<CountryChartData[]>(`${CHARTS_API}/european-trend` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getEuropeanTrendCountries(search: ChartsSearchInterface, keyFigures: string): Observable<string[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<string[]>(`${CHARTS_API}/european-trend/countries` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getTrendByCountry(search: ChartsSearchInterface, keyFigures: string): Observable<[CountryChartData[]]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<[CountryChartData[]]>(`${CHARTS_API}/trend-by-country` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getTrendByCountryCountries(search: ChartsSearchInterface, keyFigures: string): Observable<CountryChartInfo[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<CountryChartInfo[]>(`${CHARTS_API}/trend-by-country/countries` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getBenchmarkByYear(search: ChartsSearchInterface, keyFigures: string, schemaKind: string): Observable<CountryChartData[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<CountryChartData[]>(`${CHARTS_API}/benchmark-by-year` + searchParams + `&keyFigures=${keyFigures}&schemaKind=${schemaKind}`);
  }

  getBenchmarkByYearCountries(search: ChartsSearchInterface, keyFigures: string, schemaKind: string): Observable<string[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<string[]>(`${CHARTS_API}/benchmark-by-year/countries` + searchParams + `&keyFigures=${keyFigures}&schemaKind=${schemaKind}`);
  }

  getCountryVsWF(search: ChartsSearchInterface, keyFigures: string): Observable<CountryChartData[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<CountryChartData[]>(`${CHARTS_API}/country-vs-wf` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getCountryVsWFCountries(search: ChartsSearchInterface, keyFigures: string): Observable<string[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<string[]>(`${CHARTS_API}/country-vs-wf/countries` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getComposition(search: ChartsSearchInterface, keyFigures: string): Observable<CountryChartData[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<CountryChartData[]>(`${CHARTS_API}/composition` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getCompositionCountries(search: ChartsSearchInterface, keyFigures: string): Observable<string[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<string[]>(`${CHARTS_API}/composition/countries` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getVCTUpTo(search: ChartsSearchInterface): Observable<VctCollectedChartData[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<VctCollectedChartData[]>(`${CHARTS_API}/vct-up-to` + searchParams);
  }

  getVCTPUpTo(search: ChartsSearchInterface): Observable<VctCollectedChartData[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<VctCollectedChartData[]>(`${CHARTS_API}/vctp-up-to` + searchParams);
  }

  getVCTFrom(search: ChartsSearchInterface): Observable<VctCollectedChartData[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<VctCollectedChartData[]>(`${CHARTS_API}/vct-from` + searchParams);
  }

  getVCTPFrom(search: ChartsSearchInterface): Observable<VctCollectedChartData[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<VctCollectedChartData[]>(`${CHARTS_API}/vctp-from` + searchParams);
  }

  getReturnTrendByCountry(search: ChartsSearchInterface, keyFigures: string): Observable<[CountryChartData[]]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<[CountryChartData[]]>(`${CHARTS_API}/return/trend-by-country` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getCountriesReturnTrendByCountry(search: ChartsSearchInterface, keyFigures: string): Observable<CountryChartInfo[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<CountryChartInfo[]>(`${CHARTS_API}/return/trend-by-country/countries` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getReturnByYear(search: ChartsSearchInterface, keyFigures: string): Observable<[CountryChartData[]]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<[CountryChartData[]]>(`${CHARTS_API}/return/by-year` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getReturnByYearCountries(search: ChartsSearchInterface, keyFigures: string): Observable<string[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<string[]>(`${CHARTS_API}/return/by-year/countries` + searchParams + `&keyFigures=${keyFigures}`);
  }

  getRecycleByCollectionStream(search: ChartsSearchInterface): Observable<RecycleChartData[]> {
  return this.http.get<RecycleChartData[]>(`${CHARTS_API}/recycle` + KfChartsService.buildSearchParams(search));
  }

  getReturnRateWeeeSystem(search: ChartsSearchInterface): Observable<ReturnRateWeeeSystemInfo> {
  return this.http.get<ReturnRateWeeeSystemInfo>(`${CHARTS_API}/returnrate` + KfChartsService.buildSearchParams(search));
  }

  getCostBenchmark(search: ChartsSearchInterface, type: string): Observable<CostChartInfo> {
    return this.http.get<CostChartInfo>(`${CHARTS_API}/cost/${type.toLowerCase()}` + KfChartsService.buildSearchParams(search));
  }

  getCostBenchmarkDatasets(search: ChartsSearchInterface, type: string): Observable<number> {
    return this.http.get<number>(`${CHARTS_API}/cost/${type.toLowerCase()}/datasets` + KfChartsService.buildSearchParams(search));
  }

  getCostOperationalVsOverhead(search: ChartsSearchInterface): Observable<CostBenchmarkData> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<CostBenchmarkData>(`${CHARTS_API}/cost/operational-vs-overhead` + searchParams);
  }

  getCostOperationalVsOverheadCountries(search: ChartsSearchInterface): Observable<string[]> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<string[]>(`${CHARTS_API}/cost/operational-vs-overhead/countries` + searchParams);
  }

  getCostAdditional(search: ChartsSearchInterface): Observable<CostChartInfo> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<CostChartInfo>(`${CHARTS_API}/cost/additional-benchmark` + searchParams);
  }

  getCostAdditionalCategories(): Observable<Master[]> {
    return this.http.get<Master[]>(`${CHARTS_API}/cost/additional/categories`);
  }

  countCostAdditionalDatasets(search: ChartsSearchInterface): Observable<number> {
    const searchParams = KfChartsService.buildSearchParams(search);
    return this.http.get<number>(`${CHARTS_API}/cost/additional/countries` + searchParams);
  }

  downloadExcel(): Observable<any> {
    return this.http.get(`${CHARTS_API}/download-data`, {
      observe: 'response',
      responseType: 'blob'
    });
  }
}
